import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_espresso-site-9/artifacts/ittkffxj_Logo_Petit%20Hem%202.png';

function LanguageToggle({ lang, toggle, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={toggle}
      aria-label="Toggle language"
      className="relative flex items-center bg-[#111] border border-[#FFD700]/30 rounded-full p-1 w-[88px] h-[40px] transition-all duration-300 hover:border-[#FFD700]/60"
    >
      <span
        className={`absolute top-1 bottom-1 w-[38px] rounded-full bg-[#FFD700] transition-all duration-300 ease-out ${
          lang === 'fr' ? 'left-1' : 'left-[45px]'
        }`}
      />

      <span
        className={`relative z-10 flex-1 text-xs font-bold tracking-widest transition-colors duration-300 ${
          lang === 'fr' ? 'text-black' : 'text-[#B0B0B0]'
        }`}
        style={{ fontFamily: 'Bebas Neue' }}
      >
        FR
      </span>

      <span
        className={`relative z-10 flex-1 text-xs font-bold tracking-widest transition-colors duration-300 ${
          lang === 'en' ? 'text-black' : 'text-[#B0B0B0]'
        }`}
        style={{ fontFamily: 'Bebas Neue' }}
      >
        EN
      </span>
    </button>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, toggle, t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = () => {
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-black/90 backdrop-blur-xl border-b border-[#FFD700]/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <a
          href="#top"
          data-testid="header-logo"
          onClick={handleNavClick}
          className="flex items-center"
        >
          <img src={LOGO_URL} alt="Petit Hem" className="h-10 md:h-12 w-auto" />
        </a>

        <div className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          <a
            href="#menu"
            data-testid="nav-link-menu"
            onClick={handleNavClick}
            className="nav-link text-sm tracking-widest uppercase text-[#B0B0B0] hover:text-[#FFD700] transition-colors duration-300 font-medium"
          >
            {t('header.menu')}
          </a>

          <a
            href="#contact"
            data-testid="nav-link-visit-us"
            onClick={handleNavClick}
            className="nav-link text-sm tracking-widest uppercase text-[#B0B0B0] hover:text-[#FFD700] transition-colors duration-300 font-medium"
          >
            {t('header.visitUs')}
          </a>

          <LanguageToggle lang={lang} toggle={toggle} testId="lang-toggle" />
        </div>

        <div className="flex md:hidden items-center gap-4">
          <LanguageToggle lang={lang} toggle={toggle} testId="lang-toggle-mobile" />

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-white hover:text-[#FFD700] transition-colors"
            aria-label="Toggle mobile menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-nav"
          className="md:hidden bg-black/95 backdrop-blur-xl border-t border-[#222] animate-fade-in"
        >
          <div className="px-6 py-6 flex flex-col gap-6">
            <a
              href="#menu"
              data-testid="mobile-nav-link-menu"
              onClick={handleNavClick}
              className="text-sm tracking-widest uppercase text-[#B0B0B0] hover:text-[#FFD700] transition-colors font-medium"
            >
              {t('header.menu')}
            </a>

            <a
              href="#contact"
              data-testid="mobile-nav-link-visit-us"
              onClick={handleNavClick}
              className="text-sm tracking-widest uppercase text-[#B0B0B0] hover:text-[#FFD700] transition-colors font-medium"
            >
              {t('header.visitUs')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}